import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import * as React from "react"

import { cn } from "@/lib/utils"

import { formatFigure, SCOPE_LABEL, type FigureFormat, type MarkScope } from "./eval-runs-data"
import { excludedByTag, kpiName, kpiRows, type FigurePair, type Summary } from "./scope"

const tooltipClass = cn(
  "z-toast max-w-64 rounded-sm px-2 py-1",
  "bg-text text-caption text-[color:var(--surface)]",
  "shadow-e3",
  "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
  "data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0",
  "duration-120 motion-reduce:animate-none",
)

function Tip(props: { content: string; children: React.ReactElement }) {
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{props.children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side="top" sideOffset={6} className={tooltipClass}>
          {props.content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

export const NO_SCOPE_TAGS = "This run carries no scope tags, so there is nothing to exclude."

/**
 * The one control for the exclusion: it picks the figures shown and whether out-of-scope marks are
 * listed. Disabled, with the reason, on a run that was never scored in scope.
 */
export function ScopeSwitch(props: {
  checked: boolean
  onChange: (value: boolean) => void
  available: boolean
}) {
  const id = React.useId()
  const on = props.checked && props.available
  const control = (
    <span className="inline-flex items-center gap-2" tabIndex={props.available ? undefined : 0}>
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={on}
        aria-describedby={props.available ? undefined : `${id}-why`}
        disabled={!props.available}
        onClick={() => {
          props.onChange(!props.checked)
        }}
        className={cn(
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors",
          "outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
          "disabled:cursor-not-allowed disabled:opacity-50",
          on ? "border-brand bg-brand" : "border-border bg-surface-sunken",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "inline-block size-3.5 rounded-full bg-surface shadow-e3 transition-transform",
            on ? "translate-x-4" : "translate-x-0.5",
          )}
        />
      </button>
      <label htmlFor={id} className="text-body-sm text-text-2">
        Exclude undetectable marks
      </label>
      {!props.available && (
        <span id={`${id}-why`} className="sr-only">
          {NO_SCOPE_TAGS}
        </span>
      )}
    </span>
  )
  return props.available ? control : <Tip content={NO_SCOPE_TAGS}>{control}</Tip>
}

/** The tag of a mark outside the review's reach, with the classifier's reason on hover. */
export function ScopeBadge(props: { scope: MarkScope; reason: string | null }) {
  if (props.scope === "in_scope") return null
  const label = SCOPE_LABEL[props.scope]
  const badge = (
    <span className="inline-flex shrink-0 items-center rounded-full border border-border px-1.5 text-caption text-text-3">
      {label}
      {props.reason && <span className="sr-only">: {props.reason}</span>}
    </span>
  )
  return props.reason ? <Tip content={props.reason}>{badge}</Tip> : badge
}

/** A figure as the switch picked it, the other view smaller beside it. */
export function PairedFigure(props: {
  pair: FigurePair
  format: FigureFormat
  className?: string
}) {
  const { pair } = props
  return (
    <span className={props.className}>
      {formatFigure(pair.primary, props.format)}
      {pair.secondary && (
        <span className="ml-1.5 text-caption font-normal text-text-3">
          ({pair.secondary.label} {formatFigure(pair.secondary.value, props.format)})
        </span>
      )}
    </span>
  )
}

/** How many marks each tag took out of the in-scope figures, when the run says. */
export function ExcludedCounts({ summary }: { summary: Summary }) {
  const counts = excludedByTag(summary)
  if (!counts) return null
  return (
    <p className="mt-2 text-caption text-text-3">
      Left out of the in-scope figures:{" "}
      {counts.map((c) => `${SCOPE_LABEL[c.tag]} ${formatFigure(c.n, "count")}`).join(" · ")}
    </p>
  )
}

/** Recall per KPI, both ways, when the run carries it. */
export function KpiTable({ summary, exclude }: { summary: Summary; exclude: boolean }) {
  const rows = kpiRows(summary, exclude)
  if (rows.length === 0) return null
  return (
    <section className="rounded-lg border border-border bg-surface p-4" aria-labelledby="eval-kpis">
      <h2 id="eval-kpis" className="mb-2 text-body font-semibold text-text">
        Recall per KPI{exclude ? ", in-scope marks" : ", all marks"}
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-body-sm">
          <thead className="text-left text-caption font-medium text-text-2">
            <tr>
              <th className="py-1.5 pr-4">KPI</th>
              <th className="py-1.5 pr-4 text-right" title="Caught or escalated">
                Safety recall
              </th>
              <th className="py-1.5 pr-4 text-right" title="Caught only">
                Recall, flagged only
              </th>
              <th className="py-1.5 pr-4 text-right">Marks</th>
              <th className="py-1.5 text-right">Excluded</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-border-soft">
                <td className="py-1.5 pr-4 font-mono text-text">{kpiName(row.id)}</td>
                <td className="py-1.5 pr-4 text-right tabular-nums">
                  <PairedFigure pair={row.safetyRecall} format="percent" className="font-medium" />
                </td>
                <td className="py-1.5 pr-4 text-right tabular-nums text-text-2">
                  <PairedFigure pair={row.recall} format="percent" />
                </td>
                <td className="py-1.5 pr-4 text-right tabular-nums">
                  <PairedFigure pair={row.n} format="count" />
                </td>
                <td className="py-1.5 text-right tabular-nums text-text-2">
                  {formatFigure(row.excluded, "count")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
